import { API_CACHE_KEYS } from "api/cacheKeys";
import { CachePolicy } from "api/types";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";

// Types
export type ExportTaskStatus = "pending" | "completed" | "failure";

export type ExportTaskCreated = {
  task_id: string;
  task_status: ExportTaskStatus;
};

export type ExportDataResponse = ExportTaskCreated & {
  bucket: string;
  key: string;
  download_url: string;
  download_size_bytes: number;
  next: number;
};

export type CreateExportTaskParams = {
  predictorId: string;
  page?: number;
  pageSize?: number;
};

export type GetExportResultParams = {
  predictorId: string;
  taskId: string;
  page?: number;
  pageSize?: number;
};

// URL builders
const BASE_URL = `${JANZU_URL}device-api/exports`;

export const buildCreateExportTaskUrl = (params: CreateExportTaskParams): string => {
  const searchParams = new URLSearchParams();
  searchParams.append("predictor_id", params.predictorId);
  if (params.page !== undefined) {
    searchParams.append("page", params.page.toString());
  }
  if (params.pageSize !== undefined) {
    searchParams.append("page_size", params.pageSize.toString());
  }
  return `${BASE_URL}?${searchParams.toString()}`;
};

export const buildGetExportResultUrl = (params: GetExportResultParams): string => {
  const searchParams = new URLSearchParams();
  searchParams.append("predictor_id", params.predictorId);
  searchParams.append("task_id", params.taskId);
  if (params.page !== undefined) {
    searchParams.append("page", params.page.toString());
  }
  if (params.pageSize !== undefined) {
    searchParams.append("page_size", params.pageSize.toString());
  }
  return `${BASE_URL}/result?${searchParams.toString()}`;
};

// Manual fetch functions for imperative use (polling)
export const fetchCreateExportTask = async (params: CreateExportTaskParams): Promise<ExportTaskCreated> => {
  const url = buildCreateExportTaskUrl(params);
  const response = await fetch(url, {
    headers: {
      "Cache-Control": CachePolicy.NO_CACHE,
      Pragma: CachePolicy.NO_CACHE,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to create export task: ${response.statusText}`);
  }
  return response.json();
};

export const fetchGetExportResult = async (params: GetExportResultParams): Promise<ExportDataResponse> => {
  const url = buildGetExportResultUrl(params);
  const response = await fetch(url, {
    headers: {
      "Cache-Control": CachePolicy.NO_CACHE,
      Pragma: CachePolicy.NO_CACHE,
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to get export result: ${response.statusText}`);
  }
  return response.json();
};

// Query hooks (for declarative use when needed)
type UseCreateExportTaskParams = {
  params?: CreateExportTaskParams;
  enabled?: boolean;
};

const useCreateExportTask = ({ params, enabled = false }: UseCreateExportTaskParams) => {
  const url = params ? buildCreateExportTaskUrl(params) : "";

  return useCustomQuery<ExportTaskCreated>(API_CACHE_KEYS.exports.create(params), url, {
    enabled: enabled && !!params?.predictorId,
    cachePolicy: CachePolicy.NO_CACHE,
  });
};

type UseGetExportResultParams = {
  params?: GetExportResultParams;
  enabled?: boolean;
};

const useGetExportResult = ({ params, enabled = false }: UseGetExportResultParams) => {
  const url = params ? buildGetExportResultUrl(params) : "";

  return useCustomQuery<ExportDataResponse>(API_CACHE_KEYS.exports.result(params), url, {
    enabled: enabled && !!params?.taskId && !!params?.predictorId,
    cachePolicy: CachePolicy.NO_CACHE,
  });
};

// Main hook
export type UseExportsParams = {
  createTaskParams?: CreateExportTaskParams;
  resultParams?: GetExportResultParams;
  createTaskEnabled?: boolean;
  resultEnabled?: boolean;
};

export const useExports = ({
  createTaskParams,
  resultParams,
  createTaskEnabled = false,
  resultEnabled = false,
}: UseExportsParams = {}) => {
  const createTask = useCreateExportTask({ params: createTaskParams, enabled: createTaskEnabled });
  const exportResult = useGetExportResult({ params: resultParams, enabled: resultEnabled });

  return {
    // Create task operation
    createdTask: createTask.data,
    isCreatingTask: createTask.isLoading,
    createTaskError: createTask.error,
    refetchCreateTask: createTask.refetch,

    // Get result operation
    exportResult: exportResult.data,
    isLoadingResult: exportResult.isLoading,
    exportResultError: exportResult.error,
    refetchExportResult: exportResult.refetch,

    // Manual fetch functions for polling
    fetchCreateExportTask,
    fetchGetExportResult,
  };
};
